import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { LifeBuoy, Plus, Paperclip, ArrowLeft, Send, RotateCcw, Star, ShieldCheck, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  open: "bg-blue-100 text-blue-700", in_progress: "bg-indigo-100 text-indigo-700",
  waiting_customer: "bg-amber-100 text-amber-700", waiting_seller: "bg-amber-100 text-amber-700",
  escalated: "bg-rose-100 text-rose-700", resolved: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-600", reopened: "bg-purple-100 text-purple-700",
};
const label = (s) => (s || "").replace(/_/g, " ");

export default function SupportCenter({ surface = "customer" }) {
  const isSeller = surface === "seller";
  const [params] = useSearchParams();
  const [meta, setMeta] = useState({ categories: [], role: "customer" });
  const [tickets, setTickets] = useState([]);
  const [view, setView] = useState("list"); // list | new | <code>
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [dup, setDup] = useState(null);
  const [kbHits, setKbHits] = useState([]);
  const kbTimer = useRef();
  const kbSearch = (v) => {
    clearTimeout(kbTimer.current);
    if ((v || "").length < 4) { setKbHits([]); return; }
    kbTimer.current = setTimeout(() => api.get("/support/kb", { params: { q: v } }).then((r) => setKbHits((r.data.articles || []).slice(0, 3))).catch(() => {}), 250);
  };
  const [aiQ, setAiQ] = useState("");
  const [aiA, setAiA] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const askAI = async () => {
    if (!aiQ.trim()) return;
    setAiLoading(true); setAiA(null);
    try { const r = await api.post("/support/ai/ask", { question: aiQ }); setAiA(r.data); }
    catch { setAiA({ answer: "Couldn't verify that — please submit the request below.", escalate_suggested: true }); }
    finally { setAiLoading(false); }
  };
  const [form, setForm] = useState({
    category: "", subject: "", description: "",
    context_type: params.get("order") ? "order" : params.get("product") ? "product" : "general",
    suborder_id: params.get("order") || null, order_number: params.get("number") || null,
    product_id: params.get("product") || null, attachments: [],
  });

  const loadList = useCallback(() => api.get("/support/tickets").then((r) => setTickets(r.data.tickets)).catch(() => {}), []);
  useEffect(() => {
    api.get("/support/meta").then((r) => setMeta(r.data)).catch(() => {});
    loadList();
    if (params.get("order") || params.get("product")) setView("new");
  }, [loadList, params]);

  const checkDup = async (category) => {
    if (!category || (!form.suborder_id && !form.product_id)) { setDup(null); return; }
    try {
      const r = await api.get("/support/duplicate-check", { params: { category, order_id: form.suborder_id ? undefined : undefined, product_id: form.product_id || undefined } });
      setDup(r.data.duplicate ? r.data.ticket : null);
    } catch { setDup(null); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/support/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, attachments: [...f.attachments, { doc_id: r.data.doc_id, filename: r.data.filename }] }));
      toast.success("Attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const create = async () => {
    if (!form.category || !form.subject || !form.description) { toast.error("Fill category, subject and description"); return; }
    try {
      const r = await api.post("/support/tickets", form);
      toast.success(`Ticket ${r.data.ticket_code} created`);
      setForm({ category: "", subject: "", description: "", context_type: "general", suborder_id: null, product_id: null, attachments: [] });
      await loadList(); open(r.data.ticket_code);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const open = async (code) => {
    try { const r = await api.get(`/support/tickets/${code}`); setDetail(r.data); setView(code); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const send = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/support/tickets/${view}/messages`, { body: reply }); setReply(""); open(view); loadList(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const reopen = async () => { try { await api.post(`/support/tickets/${view}/reopen`); open(view); loadList(); toast.success("Reopened"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const rate = async (n) => { try { await api.post(`/support/tickets/${view}/csat`, { rating: n }); open(view); toast.success("Thanks for your feedback"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="mx-auto max-w-4xl" data-testid="support-center">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl brand-gradient-bg text-white"><LifeBuoy className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">{isSeller ? "Seller Support" : "Help & Support"}</h1>
          <p className="text-sm text-slate-500">Suffle Online · Shop. Discover. Suffle.</p>
        </div>
        {view === "list" && <button onClick={() => setView("new")} data-testid="new-ticket-btn" className="ml-auto inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> New request</button>}
        {view !== "list" && <button onClick={() => { setView("list"); loadList(); }} data-testid="back-btn" className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600"><ArrowLeft className="h-4 w-4" /> Back</button>}
      </div>

      {view === "list" && (
        <div className="space-y-3" data-testid="ticket-list">
          {tickets.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <LifeBuoy className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 font-semibold text-slate-700">No support requests yet</p>
              <p className="text-sm text-slate-500">Raise a request and our team will help you.</p>
            </div>
          ) : tickets.map((t) => (
            <button key={t.ticket_code} onClick={() => open(t.ticket_code)} data-testid={`ticket-${t.ticket_code}`} className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:shadow-md">
              <div>
                <p className="text-sm font-bold text-slate-800">{t.subject} <span className="font-normal text-slate-400">· {t.ticket_code}</span></p>
                <p className="text-xs text-slate-500">{t.category}{t.order_number ? ` · Order ${t.order_number}` : ""} · updated {new Date(t.updated_at).toLocaleDateString("en-GB")}</p>
              </div>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[t.status] || "bg-slate-100"}`}>{label(t.status)}</span>
            </button>
          ))}
        </div>
      )}

      {view === "new" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="new-ticket-form">
          {!isSeller && (
            <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50/60 p-3" data-testid="support-ai-box">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-indigo-800"><Sparkles className="h-4 w-4" /> Ask Suffle AI first</p>
              <p className="mt-0.5 text-xs text-indigo-600">Many questions (order status, refunds, returns) are answered instantly from your account.</p>
              <div className="mt-2 flex gap-2">
                <input value={aiQ} onChange={(e) => setAiQ(e.target.value)} data-testid="support-ai-input" className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="e.g. Where is my order?" />
                <button onClick={askAI} data-testid="support-ai-btn" className="rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">{aiLoading ? "…" : "Ask"}</button>
              </div>
              {aiA && <div className="mt-2 rounded-lg bg-white p-2.5 text-sm text-slate-700" data-testid="support-ai-answer">{aiA.answer}</div>}
            </div>
          )}
          <label className="text-xs font-semibold text-slate-600">Category</label>
          <select value={form.category} onChange={(e) => { setForm({ ...form, category: e.target.value }); checkDup(e.target.value); }} data-testid="category-select" className="mb-3 mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Select a category…</option>
            {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {dup && <div className="mb-3 rounded-xl bg-amber-50 p-3 text-xs text-amber-700" data-testid="dup-warning">An existing support request may already cover this issue: <b>{dup.ticket_code}</b> — {dup.subject}.</div>}
          <label className="text-xs font-semibold text-slate-600">Subject</label>
          <input value={form.subject} onChange={(e) => { setForm({ ...form, subject: e.target.value }); kbSearch(e.target.value); }} data-testid="subject-input" className="mb-3 mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Brief summary" />
          {kbHits.length > 0 && (
            <div className="mb-3 rounded-xl border border-emerald-100 bg-emerald-50/60 p-2.5" data-testid="kb-suggestions">
              <p className="text-[11px] font-semibold text-emerald-700">These help articles might solve this instantly:</p>
              {kbHits.map((a) => <button key={a.slug} onClick={() => window.open(`/help?article=${a.slug}`, "_blank")} className="mt-1 block text-left text-xs font-medium text-emerald-800 underline">{a.title}</button>)}
            </div>
          )}
          <label className="text-xs font-semibold text-slate-600">Describe your issue</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="description-input" rows={5} className="mb-3 mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Tell us what happened…" />
          <div className="mb-4 flex items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600"><Paperclip className="h-3.5 w-3.5" /> Attach<input type="file" className="hidden" onChange={upload} data-testid="attach-input" accept="image/*,application/pdf" /></label>
            {form.attachments.map((a) => <span key={a.doc_id} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{a.filename}</span>)}
          </div>
          <button onClick={create} data-testid="submit-ticket-btn" className="rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white">Submit request</button>
        </div>
      )}

      {view !== "list" && view !== "new" && detail && (
        <div className="space-y-4" data-testid="ticket-detail">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-bold text-slate-800">{detail.ticket.subject}</p><p className="text-xs text-slate-500">{detail.ticket.ticket_code} · {detail.ticket.category}</p></div>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[detail.ticket.status] || "bg-slate-100"}`}>{label(detail.ticket.status)}</span>
            </div>
            {detail.context?.order && (
              <div className="mt-3 grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-600 sm:grid-cols-3" data-testid="order-context">
                <div><span className="text-slate-400">Order</span><br />{detail.context.order.order_number}</div>
                <div><span className="text-slate-400">Status</span><br />{label(detail.context.order.order_status)}</div>
                <div><span className="text-slate-400">Payment</span><br />{label(detail.context.order.payment_status)}</div>
                <div><span className="text-slate-400">Shipping</span><br />{label(detail.context.order.shipping_status)}</div>
                <div><span className="text-slate-400">Return</span><br />{label(detail.context.order.return_status) || "—"}</div>
                <div><span className="text-slate-400">Refund</span><br />{label(detail.context.order.refund_status) || "—"}</div>
                <div className="col-span-2 sm:col-span-3"><span className="text-slate-400">Tracking</span><br />{detail.context.order.tracking_available ? `${detail.context.order.courier || "Courier"} · ${detail.context.order.awb}` : "Tracking information is currently unavailable."}</div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            {detail.messages.map((m, i) => (
              <div key={i} className={`rounded-2xl border p-3 ${m.sender_role === "super_admin" ? "border-indigo-100 bg-indigo-50/50" : "border-slate-200 bg-white"}`}>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{m.sender_role === "super_admin" ? "Support" : "You"} · {new Date(m.created_at).toLocaleString("en-GB")}</p>
                <p className="whitespace-pre-wrap text-sm text-slate-700">{m.body}</p>
                {(m.attachments || []).map((a) => <a key={a.doc_id} href="#" onClick={(e) => { e.preventDefault(); api.get(`/support/attachments/${a.doc_id}`, { responseType: "blob" }).then((r) => window.open(URL.createObjectURL(r.data))); }} className="mt-1 inline-block text-xs font-semibold text-blue-600 underline">{a.filename}</a>)}
              </div>
            ))}
          </div>

          {["resolved", "closed"].includes(detail.ticket.status) ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="resolved-actions">
              {!detail.ticket.csat && <div className="mb-3"><p className="mb-1 text-sm font-semibold text-slate-700">How was your support experience?</p>
                <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => rate(n)} data-testid={`csat-${n}`}><Star className="h-6 w-6 text-amber-400 hover:fill-amber-400" /></button>)}</div></div>}
              {detail.ticket.csat && <p className="mb-3 text-sm text-emerald-600">You rated this {detail.ticket.csat.rating}/5. Thank you!</p>}
              <button onClick={reopen} data-testid="reopen-btn" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600"><RotateCcw className="h-4 w-4" /> Reopen ticket</button>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="reply-box">
              <textarea value={reply} onChange={(e) => setReply(e.target.value)} data-testid="reply-input" rows={3} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Write a reply…" />
              <button onClick={send} data-testid="send-reply-btn" className="mt-2 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"><Send className="h-4 w-4" /> Send</button>
            </div>
          )}
          <p className="flex items-center justify-center gap-1 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Internal team notes are never shown here.</p>
        </div>
      )}
    </div>
  );
}
